<script>
import Login from './Login.svelte';
import Logout from './Logout.svelte';
import { user } from './stores.js';
import Drawer from './components/Drawer.svelte';
import Layers from './components/radar/Layers.svelte';
import Legends from './components/radar/Legends.svelte';
import Map from './components/radar/Map.svelte';
import { addMinutes, addHours, listForecastTimes } from './helpers/datetime.js';
import { createEventDispatcher } from 'svelte';
import { layers } from './components/radar/layers.js';
import { onMount, afterUpdate, beforeUpdate } from 'svelte';
import Alerts from './components/Alerts.svelte';

const dispatch = createEventDispatcher();
const frames = 8;
const width = 256;
const height = 256;

let homeY = -46352.25;
let homeX = -38976;
let isPaused = true;
let currFrame = 0;
let visibleLayers = [];
let z = 9;
let tiles = [];

function toggleVisibility(event) {
  let l = visibleLayers;
  if (l.includes(event.detail.layer)) {
    l = l.filter(a => a !== event.detail.layer);  // won't trigger component update
    visibleLayers = l; // will trigger component update
    return;
  }
  const layer = layers.filter(a => a.layer == event.detail.layer)[0];
  if (layer.interval < 0) {
    currFrame = frames-1;
  } else {
    currFrame = 0;
  }
  l.push(event.detail.layer); // won't trigger component update
  visibleLayers = l; // will trigger component update
}

let loadedFrames = [0];
const intervalID = window.setInterval(function() {
  if (isPaused) {
    return;
  }
  currFrame++;
  if (!loadedFrames.includes(currFrame)) {
    loadedFrames.push(currFrame);
  }
  if (currFrame >= frames) {
    if (!loadedFrames.includes(0)) {
      loadedFrames.push(0);
    }
    currFrame = 0;
    return;
  }
}, 1000);

let mapX;
let mapY = -46150;
if (!(window.matchMedia('only screen and (max-device-width : 1024px)').matches)) {
  mapX = -38478;
} else {
  mapX = -38830;
}

let mousedown = false;
function onmousemove(e) {
  e.preventDefault();
  if (!mousedown)
    return;
  //console.log(e.movementX, e.movementY)
  mapX = mapX + (e.movementX);
  mapY = mapY + (e.movementY);
}

let touchStartX;
let touchStartY;
function ontouchstart(e) {
  e.preventDefault();
  
  touchStartX = e.touches[0].clientX;
  touchStartY = e.touches[0].clientY;
}
function ontouchend(e) {
  e.preventDefault();
  const touchMoveX = e.changedTouches[0].clientX - touchStartX;
  const touchMoveY = e.changedTouches[0].clientY - touchStartY;
  mapX = mapX + touchMoveX;
  mapY = mapY + touchMoveY;
}

function onmousedown(e) {
  e.preventDefault(); // required for mouseup event to trigger
  mousedown = true;
}
window.addEventListener('mouseup', e => {
  mousedown = false;
});
function onupdatetiles(e) {
  tiles = e.detail.tiles;
}
function movemap(e) {
  mapX = e.detail.x;
  mapY = e.detail.y;
}
function handleanimation(e) {
  isPaused = e.detail.isPaused;
}

let clickedOnce = false;
function onclick(e) {
  if (clickedOnce) {
    if (e.changedTouches && e.changedTouches.length > 0) { // mobile compat.
        handleZoom({detail:{z: z+1, x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY}});
    } else {
        handleZoom({detail:{z: z+1, x: e.x, y: e.y}});
    }
    
    clickedOnce = false;
    return;
  }
  clickedOnce = true;
  window.setTimeout(() => {clickedOnce = false;}, 500)
}

function handleZoom(e) {
    loadedFrames = [currFrame];

    const mapBbox = map.getBoundingClientRect();
    const x = (e.detail.x || 0) - mapBbox.x;
    const y = (e.detail.y || 0) - mapBbox.y;
    const posX = (mapX - x) + ((mapBbox.width)/4);
    const posY = (mapY - y) + ((mapBbox.height)/4);

    if (e.detail.z > z) {
      mapX = posX * 2;
      mapY = posY * 2;
      homeX = homeX * 2;
      homeY = homeY * 2;
    } else {
      mapX = posX / 2;
      mapY = posY / 2;
      homeX = homeX / 2;
      homeY = homeY / 2;
    }
    z = e.detail.z;
}
</script>

<style>
:global(.noselect) {
  -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
     -khtml-user-select: none; /* Konqueror HTML */
       -moz-user-select: none; /* Old versions of Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
            user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}
.radar-container {
  display: flex;
  flex-flow: row wrap;
}
.map-container {
  width: 880px;
  height: 424px;
}
.cross {
  z-index: 3;
  position: absolute;
}
</style>

<Drawer />
<Alerts />

	<div class="container">
    <h1>Radar</h1>
    <div class="radar-container">

      <Legends z={z} frames={frames} currFrame={currFrame}
          on:togglepause={handleanimation} 
          on:togglevisibility={toggleVisibility} 
          on:zoom={handleZoom} 
          on:handleprevious={(event) => { isPaused = true; if (currFrame != 0) { currFrame--; }}} 
          on:handlenext={(event) => { isPaused = true; if (currFrame != frames) { currFrame++; }}} />

      <div draggable="false" class="map-container"
          role="button"
          aria-label="Interactive radar map"
          tabindex="0"
          style="position: relative;"
          on:mousedown={onmousedown}
          on:mousemove={onmousemove}
          on:click={onclick}
          on:keydown={(e) => e.key === 'Enter' && onclick(e)}
          on:touchend={onclick}
          on:touchstart={ontouchstart} 
          on:touchend={ontouchend}>
          <span class="cross" style="left:{mapX-homeX}px; top: {mapY-homeY}px">🞡</span>
        {#each layers as l}
          {#if visibleLayers.includes(l.layer)}
              {#each listForecastTimes(frames, l.interval ? l.interval : 3) as time, i}
                {#if currFrame == i || loadedFrames.includes(i)}
                  <Layers 
                    time={time} 
                    i={i} 
                    visible={currFrame == i} 
                    layer={l.layer} 
                    style={l.style} 
                    blend={l.blend} 
                    opacity={l.opacity}
                    x={mapX} y={mapY} z={z} width={width} height={height} />
                  {/if}
              {/each}
          {/if}
        {/each}

        <Map x={mapX} 
          y={mapY} 
          z={z} 
          width={width} 
          height={height} 
          on:updatetiles={onupdatetiles} 
          on:movemap={movemap} />
      </div>
    </div>
  </div>
